import React, { useState, useMemo } from 'react';
import type { Reservation, Table } from '../../types';
import {
  getTablesFreeForSlot,
  getMaxSingleTableCapacity,
  RESERVATION_TABLE_BUFFER_MINUTES,
} from '../../lib/reservationCapacity';

interface TableAssignmentModalProps {
  reservation: Reservation;
  tables: Table[];
  reservations: Reservation[];
  onConfirm: (updates: {
    tableId: number;
    assignedTableIds?: number[];
    requiresMerge?: boolean;
  }) => void;
  onClose: () => void;
}

export default function TableAssignmentModal({
  reservation,
  tables,
  reservations,
  onConfirm,
  onClose,
}: TableAssignmentModalProps) {
  const [tableId, setTableId] = useState(reservation.tableId ?? 0);
  const [assignedTableIds, setAssignedTableIds] = useState<number[]>(
    reservation.assignedTableIds ?? []
  );

  const freeTables = useMemo(
    () =>
      getTablesFreeForSlot(
        tables,
        reservations,
        reservation.reservationDate,
        reservation.reservationTime,
        reservation.id,
        RESERVATION_TABLE_BUFFER_MINUTES,
      ),
    [tables, reservations, reservation.reservationDate, reservation.reservationTime, reservation.id]
  );

  const maxSingleCapacity = useMemo(() => getMaxSingleTableCapacity(tables), [tables]);
  const partyFitsOneTable = reservation.partySize <= maxSingleCapacity;
  /** Minimum seats to accommodate (use adults when available, else full party) */
  const requiredSeats = reservation.adultsCount ?? reservation.partySize;
  const selectedJoinTables = useMemo(
    () => assignedTableIds.map((id) => tables.find((t) => t.id === id)).filter(Boolean) as Table[],
    [tables, assignedTableIds]
  );
  const selectedJoinCapacity = selectedJoinTables.reduce((sum, t) => sum + t.capacity, 0);
  const canSaveJoinTables =
    assignedTableIds.length >= 2 && selectedJoinCapacity >= requiredSeats;
  const canSaveSingleTable =
    tableId > 0 && assignedTableIds.length === 0 && partyFitsOneTable &&
    (tables.find((t) => t.id === tableId)?.capacity ?? 0) >= reservation.partySize;
  const canSaveOneFromMulti =
    assignedTableIds.length === 1 &&
    (tables.find((t) => t.id === assignedTableIds[0])?.capacity ?? 0) >= reservation.partySize;
  const canSave =
    canSaveSingleTable ||
    canSaveJoinTables ||
    (tableId === 0 && assignedTableIds.length === 0) ||
    canSaveOneFromMulti;

  const handleSave = () => {
    if (assignedTableIds.length >= 2 && canSaveJoinTables) {
      onConfirm({
        tableId: assignedTableIds[0],
        assignedTableIds,
        requiresMerge: true,
      });
      return;
    }
    if (canSaveSingleTable) {
      onConfirm({ tableId, assignedTableIds: [], requiresMerge: false });
      return;
    }
    if (tableId === 0 && assignedTableIds.length === 0) {
      onConfirm({ tableId: 0, assignedTableIds: [], requiresMerge: true });
      return;
    }
    if (assignedTableIds.length === 1) {
      const single = assignedTableIds[0];
      const tab = tables.find((t) => t.id === single);
      if (tab && tab.capacity >= reservation.partySize) {
        onConfirm({ tableId: single, assignedTableIds: [], requiresMerge: false });
        return;
      }
    }
    onClose();
  };

  const handleToggleJoinTable = (tid: number) => {
    const next = assignedTableIds.includes(tid)
      ? assignedTableIds.filter((id) => id !== tid)
      : [...assignedTableIds, tid];
    setAssignedTableIds(next);
    if (next.length >= 2) setTableId(0);
    if (next.length === 0) setTableId(reservation.tableId ?? 0);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-lg font-bold text-gray-800">Assign tables</h2>
          <p className="text-sm text-gray-600 mt-1">
            {reservation.customerName}
            {' · '}
            Party of {reservation.partySize}
            {reservation.adultsCount != null && (
              <> ({reservation.adultsCount} adults{reservation.kidsCount != null && reservation.kidsCount > 0 ? `, ${reservation.kidsCount} kids` : ''})</>
            )}
            {' · '}{reservation.reservationDate} at {reservation.reservationTime}
          </p>
        </div>

        <div className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Single table (optional)</label>
            <select
              value={assignedTableIds.length >= 2 ? 0 : tableId}
              onChange={(e) => {
                const id = parseInt(e.target.value, 10) || 0;
                setTableId(id);
                if (id) setAssignedTableIds([]);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
            >
              <option value={0}>No table / Use multiple below</option>
              {freeTables.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} (Capacity: {t.capacity})
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-700">
              Or select multiple tables to join (need at least {requiredSeats} seats for {reservation.adultsCount != null ? `${reservation.adultsCount} adults` : 'party'})
            </p>
            <div className="flex flex-wrap gap-2">
              {freeTables.map((t) => {
                const checked = assignedTableIds.includes(t.id);
                return (
                  <label key={t.id} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => handleToggleJoinTable(t.id)}
                      className="rounded border-gray-300 text-pink-600 focus:ring-pink-500"
                    />
                    <span className="text-sm">{t.name} ({t.capacity})</span>
                  </label>
                );
              })}
            </div>
            {assignedTableIds.length > 0 && (
              <p className="text-sm text-gray-600">
                Selected: {selectedJoinTables.map((t) => t.name).join(', ')} (total {selectedJoinCapacity} seats)
                {assignedTableIds.length >= 2 && selectedJoinCapacity < requiredSeats && (
                  <span className="text-amber-700"> — need {requiredSeats - selectedJoinCapacity} more seats.</span>
                )}
              </p>
            )}
            {canSaveJoinTables && (
              <p className="text-sm text-green-700 font-medium">✓ Enough capacity for {requiredSeats} seats.</p>
            )}
            <button
              type="button"
              onClick={() => {
                setTableId(0);
                setAssignedTableIds([]);
              }}
              className="text-sm text-gray-600 hover:text-gray-800 underline"
            >
              Clear — merge on arrival (no specific tables)
            </button>
          </div>
        </div>

        <div className="p-4 border-t border-gray-200 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave}
            className="px-4 py-2 bg-pink-500 text-white rounded-md hover:bg-pink-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Save assignment
          </button>
        </div>
      </div>
    </div>
  );
}
